import { useEffect, useMemo } from 'react';

import { logError } from '../lib/logger';
import { logReadFailure } from '../lib/net/logReadFailure';
import { timeZoneOf } from '../lib/organization/timeZone';
import { supabase } from '../lib/supabase';
import {
  type OrganizationProfile,
  type OrganizationState,
  parseOrganizationProfile,
  useOrganizationStore,
} from '../store/organizationStore';

/**
 * The company record and its members (`organization_profile()`), read once for the whole app and
 * kept in `useOrganizationStore` (PLAN-GENERICITE.md § 3.2). Read by Settings > Company and team, by
 * every screen that builds a link to the company's hosts or site (`src/lib/organization/hosts.ts`), and
 * by every screen that computes a day or shows a date in the company's zone (`useOrganizationTimeZone`).
 */

/**
 * The one call made here. `organization_profile()` is newer than `src/types/supabase.ts`, which is
 * not regenerated for it (a regeneration drops the hand-kept nullability fixes): the answer stays
 * `unknown` until the store's schema narrows it. Any Supabase client fits this structural subset.
 */
export interface OrganizationRpcClient {
  rpc(fn: string): PromiseLike<{ data: unknown; error: { message: string; code?: string } | null }>;
}

const MESSAGES = {
  readFailed: "La fiche de l'entreprise n'a pas pu être lue. Réessayez dans un instant.",
  notApproved: "La fiche de l'entreprise est réservée aux utilisateurs approuvés.",
  unreadable: "La fiche de l'entreprise reçue n'a pas la forme attendue.",
} as const;

let latestRead = 0;

/**
 * Reads the record into the store. Every call reads (a screen that has just saved asks for the new
 * record); when reads overlap, the last one started is the one kept. Never throws: a failure becomes
 * the store's `error` state, logged with its context.
 */
export async function loadOrganization(client: OrganizationRpcClient = supabase): Promise<void> {
  const read = ++latestRead;
  useOrganizationStore.getState().startLoading();
  let answer: Awaited<ReturnType<OrganizationRpcClient['rpc']>>;
  try {
    answer = await client.rpc('organization_profile');
  } catch (err) {
    answer = { data: null, error: { message: err instanceof Error ? err.message : String(err) } };
  }
  if (read !== latestRead) return;

  const store = useOrganizationStore.getState();
  if (answer.error) {
    logReadFailure('organization', 'organization_profile() read failed', answer.error);
    store.setError(answer.error.code === '42501' ? MESSAGES.notApproved : MESSAGES.readFailed);
    return;
  }
  const parsed = parseOrganizationProfile(answer.data);
  if (!parsed.ok) {
    logError('organization', 'organization_profile() does not match its schema', new Error(parsed.issues.join('; ')), {
      issues: parsed.issues,
    });
    store.setError(MESSAGES.unreadable);
    return;
  }
  store.setProfile(parsed.profile);
}

/** For `onClick={refresh}`: an event must never reach `loadOrganization` as its client. */
const refresh = (): Promise<void> => loadOrganization();

export type OrganizationView = Exclude<OrganizationState, { status: 'idle' }> & {
  /** Reads the record again (after a save); the current one stays on screen meanwhile. */
  readonly refresh: () => Promise<void>;
};

/**
 * The record for a screen: `loading`, `ready` (with `organization`), `empty` (no organization yet:
 * an empty state with its explanation, not an error) or `error` (with `message`). The first screen
 * that mounts starts the read; the others share it.
 */
export function useOrganization(): OrganizationView {
  const state = useOrganizationStore((s) => s.state);
  useEffect(() => {
    if (useOrganizationStore.getState().state.status === 'idle') void loadOrganization();
  }, []);
  // The same object while the state is the same: safe in a dependency array.
  return useMemo<OrganizationView>(
    () => (state.status === 'idle' ? { status: 'loading', refresh } : { ...state, refresh }),
    [state],
  );
}

/**
 * The record once it is read, else null (loading, none, unreadable): for a screen that only builds
 * links from it and leaves a link out while it is null.
 */
export function useOrganizationProfile(): OrganizationProfile | null {
  const view = useOrganization();
  return view.status === 'ready' ? view.organization : null;
}

/**
 * The company's time zone for a screen (`src/lib/organization/timeZone.ts`): the record's once it is
 * read, the platform default meanwhile (loading, none, unreadable). Starts the read like
 * `useOrganization()`, and re-renders the screen when the record arrives.
 */
export function useOrganizationTimeZone(): string {
  return timeZoneOf(useOrganizationProfile());
}

/**
 * The record for a store action (outside React): the one in the store, read first when it is not there
 * (never asked, or a previous read failed). Null when there is none or it cannot be read; never throws.
 */
export async function currentOrganization(client: OrganizationRpcClient = supabase): Promise<OrganizationProfile | null> {
  if (useOrganizationStore.getState().state.status !== 'ready') await loadOrganization(client);
  const state = useOrganizationStore.getState().state;
  return state.status === 'ready' ? state.organization : null;
}
